import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Cpu, Check, PlayCircle,
  Globe, Magnet, Workflow, BarChart3, Bot, Users,
  ShieldCheck, Smartphone, Database, Lock, Zap, Layers,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";

/* -------------------- module data -------------------- */

const MODULES = [
  {
    id: "site",
    n: "01",
    icon: Globe,
    name: "Site",
    tagline: "A website built to convert, not just to look at.",
    desc: "A fast, conversion-focused website with a local-SEO foundation. Branded to your business, edited through Growth OS, with built-in forms and click-to-call.",
    inside: ["Page builder, branded to you", "Local-SEO settings (schema, locations)", "Quote forms, click-to-call, after-hours capture", "A/B test variants on key pages"],
    outcome: "More of your existing traffic turns into booked work.",
  },
  {
    id: "capture",
    n: "02",
    icon: Magnet,
    name: "Capture",
    tagline: "Every lead, every channel, in one inbox.",
    desc: "Web forms, phone calls, missed-call SMS, paid ads, referrals. Captured, tagged, attributed, and routed instantly. The leak between marketing spend and booked work, sealed.",
    inside: ["Web form integration & smart routing", "Missed-call SMS auto-response", "Google & Meta ad lead sync", "Source attribution & channel reporting"],
    outcome: "Zero leads lost. Every dollar of marketing spend traceable to revenue.",
  },
  {
    id: "crm",
    n: "03",
    icon: Workflow,
    name: "CRM",
    tagline: "The follow-up that wins the work, automated.",
    desc: "A CRM configured to how your trade actually operates. Contact records, opportunity stages, automated workflows, customer history, photos, notes, all in one record.",
    inside: ["Contact & opportunity records", "Job-lifecycle automation", "Quote follow-up sequences (SMS, email, tasks)", "Customer history with photos and notes"],
    outcome: "Quote-to-job conversion typically lifts 2 to 4 times within 90 days.",
  },
  {
    id: "dashboards",
    n: "04",
    icon: BarChart3,
    name: "Dashboards",
    tagline: "The whole business, on one screen.",
    desc: "A real-time KPI cockpit showing leads, quotes, conversion, revenue, margin by job type, and team performance. Built for owners who manage by signal, not feel.",
    inside: ["Live lead, quote and revenue funnel", "Margin & profitability by job type", "Team performance & review velocity", "Web and mobile dashboards"],
    outcome: "You decide by data. The numbers find the leaks before they get expensive.",
  },
  {
    id: "ai",
    n: "05",
    icon: Bot,
    name: "AI",
    tagline: "An always-on assistant that books work.",
    desc: "A chat (and optional voice) assistant trained on your business: services, pricing tiers, service area and FAQs. Qualifies, answers, books, and hands over to a human cleanly.",
    inside: ["Custom-trained web chat", "Optional AI voice receptionist (after-hours)", "FAQ knowledge base, owned by you", "Human handover rules for complex enquiries"],
    outcome: "After-hours capture lifts. Owner phone-time drops.",
  },
  {
    id: "ops",
    n: "06",
    icon: Users,
    name: "Ops",
    tagline: "The command surface for the team that runs it.",
    desc: "Where our outsourced execution team operates the system day-to-day. Task queues, daily CRM hygiene, ad management, review chasing, content posting, weekly reports.",
    inside: ["Daily task queue & SLAs", "CRM hygiene & quote follow-up calls", "Ad campaign management (Google & Meta)", "Review chasing, content posting, weekly reports"],
    outcome: "60 to 80 percent reduction in owner and partner admin hours each week.",
  },
];

const PRINCIPLES = [
  { icon: Layers, k: "One platform", v: "Six modules, one branded login. Not five vendor tools stitched together with duct tape." },
  { icon: Database, k: "Your data, owned", v: "Exportable, portable, fully yours. If you ever leave, the data and accounts go with you." },
  { icon: Lock, k: "Built-in security", v: "Role-based access, audit trails, encryption in transit, modern auth." },
  { icon: Zap, k: "Real-time", v: "Lead and operational data is live. Financial metrics sync nightly from your accounting system." },
  { icon: Smartphone, k: "Mobile-native", v: "Owner dashboard and team approvals run cleanly on a phone, not just desktop." },
  { icon: ShieldCheck, k: "Run by the firm", v: "Growth OS is the software. Toulios is the team that operates it alongside you." },
];

const COMPARE = [
  { row: "Modern website + hosting", growthOS: true, vendorSoup: true, note: "" },
  { row: "Multi-channel lead capture & routing", growthOS: true, vendorSoup: false, note: "Usually requires Zapier glue" },
  { row: "CRM with trade-specific automation", growthOS: true, vendorSoup: false, note: "Generic CRMs need heavy config" },
  { row: "Real-time dashboards across business", growthOS: true, vendorSoup: false, note: "Separate BI tool, separate fee" },
  { row: "AI assistant trained on your business", growthOS: true, vendorSoup: false, note: "Off-the-shelf bots don't fit" },
  { row: "Outsourced team operating it for you", growthOS: true, vendorSoup: false, note: "Need to hire and train" },
  { row: "Single monthly invoice", growthOS: true, vendorSoup: false, note: "Five to eight vendor bills" },
  { row: "Your data exportable, ownable", growthOS: true, vendorSoup: false, note: "Often locked to platform" },
];

const ROLES = [
  { role: "Owner / Operator", access: "Full visibility, approvals, strategic dashboards, monthly reports." },
  { role: "Office Admin", access: "CRM, scheduling, quote follow-up tools, customer history." },
  { role: "Sales / Estimator", access: "Lead pipeline, quote workflow, mobile access on jobs." },
  { role: "Field Team", access: "Job details, customer photos, customer comms via shared inbox." },
  { role: "Toulios Ops Team", access: "Daily task queue, ad management, content posting, reporting." },
];

const FAQS = [
  {
    q: "Is Growth OS something I have to host or install?",
    a: "No. Growth OS is fully managed. You sign in through the browser or a mobile app. We handle hosting, security, updates and uptime. There is nothing to install on your computers.",
  },
  {
    q: "How is Growth OS different from a generic CRM?",
    a: "A generic CRM is one module. Growth OS includes your website, lead capture, CRM, dashboards, AI assistant and ops surface in one connected product. The savings show up where the modules talk to each other, not in any one module on its own.",
  },
  {
    q: "Can I use it without engaging Toulios for ongoing operations?",
    a: "Yes. Growth OS comes with every Growth System engagement. After the 60-day build, you can choose to keep operations in-house or have our outsourced team run it for you (Full Execution tier). Either way, the platform is the same.",
  },
  {
    q: "Where does my data live?",
    a: "On Australian or Australian-adjacent cloud infrastructure (AWS / Microsoft Azure regions). Data is encrypted in transit, role-based access is enforced, and you can export everything at any time.",
  },
  {
    q: "What if I already pay for a CRM, ad platform or website tool?",
    a: "We audit your existing stack on the Strategy Call. Where a tool is genuinely working, we keep it. Where the math says replacing it pays back inside 12 months, we migrate. No religious wars.",
  },
  {
    q: "When can I see a live demo?",
    a: "Book a Strategy Call. We walk you through Growth OS live, configured to your industry. The call is complimentary and runs about 45 to 60 minutes.",
  },
];

/* -------------------- stylised mockup frames -------------------- */

function Browser({ children, title = "growthos.toulios.com.au" }) {
  return (
    <div className="relative border border-white/[0.08] bg-[#0a0a0a]">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.06]">
        <span className="w-2 h-2 rounded-full bg-zinc-700" />
        <span className="w-2 h-2 rounded-full bg-zinc-700" />
        <span className="w-2 h-2 rounded-full bg-zinc-700" />
        <span className="ml-3 text-[10px] text-zinc-600 tracking-tight">{title}</span>
      </div>
      <div className="p-4 md:p-6">{children}</div>
    </div>
  );
}

function ModuleMockup({ id }) {
  if (id === "site") {
    return (
      <Browser>
        <div className="space-y-3">
          <div className="h-2 w-20 bg-gold/40" />
          <div className="h-5 w-3/4 bg-white/10" />
          <div className="h-5 w-1/2 bg-white/10" />
          <div className="h-2 w-2/3 bg-white/[0.06] mt-3" />
          <div className="h-2 w-1/2 bg-white/[0.06]" />
          <div className="mt-5 flex gap-2">
            <div className="h-7 w-24 bg-gold" />
            <div className="h-7 w-24 border border-white/15" />
          </div>
        </div>
      </Browser>
    );
  }
  if (id === "capture") {
    return (
      <Browser>
        <div className="space-y-3">
          <div className="overline text-zinc-500">New lead · 4 min ago</div>
          {["Name", "Phone", "Service", "When"].map((l) => (
            <div key={l} className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-600">{l}</span>
              <span className="h-2 w-32 bg-white/[0.08]" />
            </div>
          ))}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-[10px] text-gold">SOURCE</span>
            <span className="px-2 py-0.5 border border-gold/40 text-gold text-[10px]">Google Ads</span>
          </div>
        </div>
      </Browser>
    );
  }
  if (id === "crm") {
    return (
      <Browser>
        <div className="grid grid-cols-3 gap-3">
          {["Lead", "Quoted", "Won"].map((stage, i) => (
            <div key={stage} className="space-y-2">
              <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">{stage}</div>
              {Array.from({ length: 3 - i }).map((_, j) => (
                <div key={`${stage}-card-${j}`} className="bg-white/[0.04] border border-white/[0.06] p-2 space-y-1">
                  <div className="h-2 w-3/4 bg-white/10" />
                  <div className="h-1.5 w-1/2 bg-white/[0.07]" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Browser>
    );
  }
  if (id === "dashboards") {
    return (
      <Browser>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {["Leads", "Quotes", "Won"].map((k, i) => (
              <div key={k} className="border border-white/[0.06] p-2">
                <div className="text-[10px] tracking-[0.16em] uppercase text-zinc-500">{k}</div>
                <div className="font-display text-lg mt-1">{[124, 88, 41][i]}</div>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-1.5 h-16">
            {[40, 55, 38, 70, 62, 80, 95].map((h, i) => (
              <div key={`bar-${i}-${h}`} className="flex-1 bg-gold/70" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
      </Browser>
    );
  }
  if (id === "ai") {
    return (
      <Browser>
        <div className="space-y-3">
          <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Live chat · just now</div>
          <div className="max-w-[80%] bg-white/[0.05] border border-white/[0.06] p-3 text-[11px] text-zinc-300 leading-relaxed">
            Hi, can you do same-day hot-water replacement in Manly?
          </div>
          <div className="max-w-[80%] ml-auto bg-gold/15 border border-gold/30 p-3 text-[11px] text-zinc-200 leading-relaxed">
            Yes. We have a 2-hour window today at 3pm. Want me to lock it in?
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Bot className="w-3 h-3 text-gold" />
            <span className="text-[10px] text-gold">Growth OS assistant</span>
          </div>
        </div>
      </Browser>
    );
  }
  if (id === "ops") {
    return (
      <Browser>
        <div className="space-y-2">
          {[
            { t: "Follow up: 14 open quotes", s: "Due today" },
            { t: "Run Google Ads optimisation", s: "Weekly" },
            { t: "Post review request: 8 customers", s: "Daily" },
            { t: "Reply to 3 inbound DMs", s: "Today" },
          ].map((task) => (
            <div key={task.t} className="flex items-center justify-between border border-white/[0.06] p-2.5">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 border border-gold/60" />
                <span className="text-[11px] text-zinc-200">{task.t}</span>
              </div>
              <span className="text-[9px] tracking-[0.16em] uppercase text-zinc-500">{task.s}</span>
            </div>
          ))}
        </div>
      </Browser>
    );
  }
  return null;
}

/* -------------------- page -------------------- */

export default function GrowthOS() {
  return (
    <>
      {/* HERO */}
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="os-hero">
        <div className="overline flex items-center gap-2">
          <Cpu className="w-3.5 h-3.5" /> The software product · powers The Toulios Growth System
        </div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          Toulios <span className="text-gold">Growth OS</span>.
          <br className="hidden md:block" /> One platform. Six modules. One business, running.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          Growth OS is the branded operating platform that powers every Toulios
          Growth System engagement. Site, capture, CRM, dashboards, AI and ops,
          in a single login, configured to your business and operated alongside
          your team.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/contact" className="btn-gold" data-testid="os-cta-demo">
            Book a live demo <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/growth-system" className="btn-ghost">
            See the engagement model
          </Link>
        </div>

        {/* stats strip */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 max-w-4xl">
          {[
            { k: "1 login", v: "Six modules, one branded platform." },
            { k: "Live", v: "Lead and ops data in real-time." },
            { k: "Owned", v: "Exportable, portable, yours." },
            { k: "Operated", v: "By the firm that built it." },
          ].map((m) => (
            <div key={m.v} className="border-l border-white/10 pl-4">
              <div className="text-xl md:text-2xl font-display text-white">{m.k}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{m.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SCREENCAST SLOT */}
      <section className="section-y" data-testid="os-screencast">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="Product tour"
              title="See it in motion."
              description="A 2-minute walkthrough of Growth OS, configured for a real trade business. Coming soon. In the meantime, book a live demo and we will show you the same thing tailored to your trade."
            />
          </Reveal>

          <div className="mt-12 relative max-w-5xl mx-auto" data-testid="os-screencast-frame">
            <div
              aria-hidden
              className="absolute -inset-px bg-gradient-to-br from-gold/25 via-transparent to-gold/10 pointer-events-none"
            />
            <div className="relative border border-white/[0.08] bg-[#0a0a0a]">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/[0.06]">
                <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                <span className="ml-3 text-[11px] text-zinc-500 tracking-tight">growthos.toulios.com.au · 2-minute tour</span>
              </div>
              <div className="relative aspect-video flex items-center justify-center bg-[radial-gradient(circle_at_50%_40%,rgba(212,175,55,0.18),transparent_60%)]">
                <div className="relative flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full border border-gold flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <PlayCircle className="w-9 h-9 text-gold" />
                  </div>
                  <div className="text-sm text-zinc-300 tracking-wide">Tour coming soon</div>
                  <div className="overline">2 min walkthrough</div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-zinc-500">
            Want to skip the wait? Book a live demo and we will tour Growth OS with you on a call.
          </p>
        </div>
      </section>

      {/* WHAT'S INSIDE, MODULES */}
      <section
        id="modules"
        className="section-y bg-[#070707] border-y border-white/[0.05] scroll-mt-24"
        data-testid="os-modules-section"
      >
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="What's inside"
              title="Six modules, one connected product."
              description="Each module solves a category. The compounding value comes from how they talk to each other behind the scenes."
            />
          </Reveal>

          <div className="mt-20 space-y-24 md:space-y-32">
            {MODULES.map((m, i) => {
              const flip = i % 2 === 1;
              return (
                <Reveal key={m.id}>
                  <article
                    id={m.id}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center scroll-mt-28"
                    data-testid={`os-module-section-${m.id}`}
                  >
                    <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                      <div className="flex items-center gap-3">
                        <span className="overline">{m.n}</span>
                        <m.icon className="w-5 h-5 text-gold" />
                      </div>
                      <h2 className="mt-6 text-3xl md:text-4xl font-display font-light tracking-tight">{m.name}</h2>
                      <p className="mt-3 text-lg text-gold leading-relaxed">{m.tagline}</p>
                      <p className="mt-5 text-zinc-400 leading-relaxed">{m.desc}</p>
                      <ul className="mt-8 space-y-3 text-sm text-zinc-300">
                        {m.inside.map((b) => (
                          <li key={b} className="flex items-start gap-3">
                            <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-8 border-l border-gold/40 pl-5">
                        <div className="overline">Outcome</div>
                        <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{m.outcome}</p>
                      </div>
                    </div>

                    <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                      <div className="relative">
                        <div
                          aria-hidden
                          className="absolute -inset-px bg-gradient-to-br from-gold/15 via-transparent to-transparent pointer-events-none"
                        />
                        <ModuleMockup id={m.id} />
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="section-y" data-testid="os-principles">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="How it's built"
              title="Six principles behind Growth OS."
              description="The non-negotiables that make this product different from a stitched-together vendor stack."
            />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.05]">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.k} delay={i * 70} className="bg-ink p-8 md:p-10">
                <p.icon className="w-5 h-5 text-gold" />
                <div className="mt-6 overline">{p.k}</div>
                <p className="mt-4 text-zinc-300 leading-relaxed text-[15px]">{p.v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="os-compare">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="Growth OS vs vendor sprawl"
              title="One platform, or five vendor logins?"
              description="What you get inside Growth OS, versus what you would have to glue together yourself with off-the-shelf tools."
            />
          </Reveal>
          <div className="mt-16 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
            <table className="w-full min-w-[760px] border-collapse" data-testid="os-compare-table">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left py-5 pr-6 align-bottom">
                    <span className="overline">Capability</span>
                  </th>
                  <th className="text-center py-5 px-4 align-bottom min-w-[140px]">
                    <div className="font-display text-base md:text-lg tracking-tight text-gold">Growth OS</div>
                    <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-zinc-500">One platform</div>
                  </th>
                  <th className="text-center py-5 px-4 align-bottom min-w-[140px]">
                    <div className="font-display text-base md:text-lg tracking-tight">Vendor stack</div>
                    <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-zinc-500">5 to 8 tools</div>
                  </th>
                  <th className="text-left py-5 pl-4 align-bottom min-w-[200px] text-[11px] tracking-[0.18em] uppercase text-zinc-500">Notes</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((r) => (
                  <tr key={r.row} className="border-b border-white/[0.04]">
                    <td className="py-4 pr-6 text-sm text-zinc-300">{r.row}</td>
                    <td className="py-4 px-4 text-center">
                      <Check className="w-4 h-4 text-gold mx-auto" />
                    </td>
                    <td className="py-4 px-4 text-center">
                      {r.vendorSoup ? (
                        <Check className="w-4 h-4 text-zinc-500 mx-auto" />
                      ) : (
                        <span className="text-zinc-700">—</span>
                      )}
                    </td>
                    <td className="py-4 pl-4 text-xs text-zinc-500 leading-relaxed">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="section-y" data-testid="os-roles">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Who uses it"
              title="One platform. Different lenses."
              description="Role-based access means each person sees the surface they need. Owners get the strategic view. Field teams get the operational one."
            />
          </div>
          <div className="lg:col-span-7 space-y-px bg-white/[0.05]">
            {ROLES.map((r) => (
              <div key={r.role} className="bg-ink grid grid-cols-12 gap-4 p-6 md:p-8" data-testid={`os-role-${r.role.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <div className="col-span-12 sm:col-span-4 overline">{r.role}</div>
                <div className="col-span-12 sm:col-span-8 text-zinc-300 leading-relaxed text-[15px]">{r.access}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW YOU GET ACCESS */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="os-access">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="How you get access"
              title="Growth OS comes with every engagement."
              description="You don't sign up. You don't pay a per-seat licence. Growth OS is included in the Toulios Growth System build and runs as long as you stay engaged."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.05]">
            {[
              { n: "01", t: "Strategy Call", d: "We diagnose your business and confirm fit. Includes a live walkthrough of Growth OS configured for your trade." },
              { n: "02", t: "Build Sprint (5 weeks)", d: "Your branded Growth OS is configured: site, CRM, workflows, dashboards, AI assistant, role-based access." },
              { n: "03", t: "Go-live & Run", d: "Cutover and team training. From week 8, the OS is live. Optionally, our team runs the ops surface for you." },
            ].map((s) => (
              <div key={s.n} className="bg-ink p-8 md:p-10" data-testid={`os-access-${s.n}`}>
                <div className="overline">{s.n}</div>
                <h3 className="mt-6 text-xl md:text-2xl font-display font-light tracking-tight">{s.t}</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed text-[15px]">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/pricing" className="btn-outline-white">See pricing</Link>
            <Link to="/contact" className="btn-gold" data-testid="os-access-cta">
              Book a live demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y" data-testid="os-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="Growth OS questions, answered."
              description="The questions every operator asks before signing into the platform."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="os-faq" />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-y" data-testid="os-final-cta">
        <div className="container-x">
          <div className="relative overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#0d0d0d] to-[#070707] p-10 md:p-16 lg:p-20">
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.18), transparent 70%)" }}
            />
            <div aria-hidden className="absolute inset-x-0 top-0 h-px gold-line" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="overline">See it for yourself</div>
                <h3 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-display font-light tracking-tight text-balance">
                  Book a live demo. 45 minutes. Your trade, your business, your numbers.
                </h3>
                <p className="mt-6 max-w-2xl text-zinc-400 leading-relaxed">
                  We walk you through Growth OS configured for your business
                  type. No slide deck. Just the product, the numbers, and a
                  straight answer on whether it fits.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                <Link to="/contact" className="btn-gold" data-testid="os-final-cta-book">
                  Book a Live Demo
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/growth-system" className="btn-outline-white">
                  See the engagement
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
